import React from 'react';
import { motion } from 'framer-motion';
import { SmartLink, SectionShell, RichText, isDarkBg } from '../shared';
import { ICON_MAP } from './InfoSections';

const Icon = ({ name, className }) => {
  const C = (name && ICON_MAP[name]) || ICON_MAP.Award;
  return <C className={className} strokeWidth={1.5} />;
};

const LogoCard = ({ image, name, link, featured }) => {
  const inner = <img src={image?.url} alt={image?.alt_text || name} className={`${featured ? 'h-24' : 'h-14'} max-w-full object-contain`} loading="lazy" />;
  const cls = `flex items-center justify-center bg-white border border-neutral-200 rounded-xl hover:shadow-md transition-all ${featured ? 'p-6 h-32' : 'p-4 h-24'}`;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
      {link ? <SmartLink to={link} className={cls}>{inner}</SmartLink> : <div className={cls}>{inner}</div>}
    </motion.div>
  );
};

export const SplitLogosSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const logosLeft = section.layout === 'logos_left';
  const logos = (p.logos || []).filter((l) => l.image?.url);

  const text = (
    <div>
      {p.eyebrow && <span className={`text-xs font-medium tracking-wider uppercase ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
      {p.title && <h2 className={`mt-2 !text-3xl !font-semibold ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>}
      <RichText html={p.body} className={`mt-4 leading-relaxed ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
      <div className="mt-6 space-y-4">
        {(p.items || []).map((it, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex gap-3" data-testid={`split-benefit-${i}`}>
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-neutral-100 rounded-lg border border-neutral-200 text-neutral-900"><Icon name={it.icon} className="w-6 h-6" /></div>
            <div>
              <h4 className={`!font-medium !text-sm mb-0.5 ${dark ? 'text-white' : 'text-neutral-900'}`}>{it.title}</h4>
              <p className={`text-sm ${dark ? 'text-neutral-400' : 'text-neutral-500'}`}>{it.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const logoCol = (
    <div>
      {p.logos_eyebrow && <span className={`text-xs font-medium tracking-wider uppercase ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.logos_eyebrow}</span>}
      {p.logos_title && <h3 className={`mt-2 !text-3xl !font-medium mb-6 ${dark ? 'text-white' : 'text-neutral-800'}`}>{p.logos_title}</h3>}
      {p.featured_logo?.url && <div className="mb-4"><LogoCard image={p.featured_logo} name={p.featured_logo.alt_text} link={p.featured_logo_link} featured /></div>}
      {logos.length > 0 && <div className="grid grid-cols-3 gap-4">{logos.map((l, i) => <LogoCard key={i} {...l} />)}</div>}
    </div>
  );

  return (
    <SectionShell section={{ ...section, style: { padding: 'compact', ...(section.style || {}) } }}>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        {logosLeft ? <>{logoCol}{text}</> : <>{text}{logoCol}</>}
      </div>
    </SectionShell>
  );
};
